import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import JalaliDateField from '../components/common/JalaliDateField';
import {
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  CalendarMonth as CalendarIcon,
  Wallet as WalletIcon,
  Payments as PaymentsIcon,
  CurrencyExchange as ExchangeIcon,
  Lock as LockIcon,
  PointOfSale as PosIcon,
  Percent as PercentIcon,
  EventNote as EventNoteIcon,
  ReceiptLong as LedgerIcon,
  Assessment as ReportIcon,
  AccountBalanceWallet as StatementIcon,
  OpenInNew as DrillIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import moment from 'moment-jalaali';
import financeService from '../services/financeService';
import financeLedgerService, {
  LedgerSummary,
  SOURCE_LABELS,
  LedgerSource,
} from '../services/financeLedgerService';
import ProtectedRoute from '../components/common/ProtectedRoute';
import './EntityManagement.css';

type CurrencyBase = 'IRR' | 'IRT';

const toPersianDigits = (s: string | number): string =>
  String(s).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const FinanceDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState<'7d' | '30d' | '90d' | 'custom'>('30d');
  const [unit, setUnit] = useState<CurrencyBase>('IRT');
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState<any>(null);
  const [calendar, setCalendar] = useState<any[]>([]);
  const [ledgerSummary, setLedgerSummary] = useState<LedgerSummary | null>(null);
  const [ledgerDenied, setLedgerDenied] = useState(false);

  // بازه دلخواه شمسی — فقط وقتی range === 'custom' اعمال می‌شود
  const [customFromJ, setCustomFromJ] = useState('');
  const [customToJ, setCustomToJ] = useState('');
  const [appliedCustom, setAppliedCustom] = useState<{ from: string; to: string } | null>(null);

  const fromTo = useMemo(() => {
    if (range === 'custom' && appliedCustom) {
      const fromM = moment(appliedCustom.from, 'jYYYY/jMM/jDD').startOf('day');
      const toM = moment(appliedCustom.to, 'jYYYY/jMM/jDD').endOf('day');
      if (fromM.isValid() && toM.isValid()) {
        return { from: fromM.toISOString(), to: toM.toISOString() };
      }
    }
    const to = new Date();
    const days = range === '7d' ? 7 : range === '90d' ? 90 : 30;
    const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [range, appliedCustom]);

  const applyCustomRange = () => {
    const fromM = moment(customFromJ.trim(), 'jYYYY/jMM/jDD');
    const toM = moment(customToJ.trim(), 'jYYYY/jMM/jDD');
    if (!fromM.isValid() || !toM.isValid()) return;
    setRange('custom');
    setAppliedCustom({ from: customFromJ.trim(), to: customToJ.trim() });
  };

  // Jalali strings of the active range — reused by drill-down links
  const jalaliRange = useMemo(() => ({
    from: moment(fromTo.from).format('jYYYY/jMM/jDD'),
    to: moment(fromTo.to).format('jYYYY/jMM/jDD'),
  }), [fromTo]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ov, cal] = await Promise.all([
        financeService.getOverview({ ...fromTo, unit, scale: 'raw' }),
        financeService.getReleaseCalendar(30),
      ]);
      setOverview(ov);
      setCalendar(cal || []);
    } finally {
      setLoading(false);
    }

    // گردش دفتر — جدا fetch می‌شود تا نبودِ پرمیشن ledger کل صفحه را نشکند
    try {
      const sum = await financeLedgerService.getLedgerSummary(
        { from: fromTo.from, to: fromTo.to },
        { silentForbidden: true },
      );
      setLedgerSummary(sum);
      setLedgerDenied(false);
    } catch {
      setLedgerSummary(null);
      setLedgerDenied(true);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromTo, unit]);

  const m = overview?.metrics || {};
  const unitLabel = unit === 'IRR' ? 'ریال' : 'تومان';

  /** All backend amounts are Toman; Rial view = ×10. Scales to هزار/میلیون/میلیارد. */
  const fmt = (raw: number | null | undefined): string => {
    if (raw == null) return '—';
    let amount = Number(raw);
    if (!isFinite(amount)) return '—';
    if (unit === 'IRR') amount *= 10;

    const abs = Math.abs(amount);
    let value = amount;
    let scale = '';
    if (abs >= 1_000_000_000) { value = amount / 1_000_000_000; scale = 'میلیارد'; }
    else if (abs >= 1_000_000) { value = amount / 1_000_000; scale = 'میلیون'; }
    else if (abs >= 1_000) { value = amount / 1_000; scale = 'هزار'; }

    const num = new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 2 }).format(value);
    return scale ? `${num} ${scale} ${unitLabel}` : `${num} ${unitLabel}`;
  };

  const fmtCount = (n: number | null | undefined): string =>
    n == null ? '—' : new Intl.NumberFormat('fa-IR').format(n);

  /** Navigate to the ledger with pre-applied filters (drill-down). */
  const drill = (params: Record<string, string>) => {
    const qs = new URLSearchParams({ from: jalaliRange.from, to: jalaliRange.to, ...params });
    navigate(`/finance/transactions?${qs.toString()}`);
  };

  // -------------------------------------------------------------------------
  // Metric definitions — each row: عنوان، مقدار، «این عدد دقیقاً چیست؟»، drill
  // -------------------------------------------------------------------------

  interface MetricRow {
    title: string;
    value: React.ReactNode;
    desc: string;
    color?: string;
    drillParams?: Record<string, string>;
  }

  // ✅ فروش خالص از خود بک‌اند (Σ totalPrice اقلام فعال) — قبلاً GMV − تخفیف در فرانت حساب می‌شد که با اقلام قدیمی نمی‌خواند
  const netSales = m.netSales != null ? m.netSales : ((m.gmv != null && m.totalDiscount != null) ? m.gmv - m.totalDiscount : null);

  const salesRows: MetricRow[] = [
    {
      title: 'ارزش ناخالص معاملات (GMV)',
      value: fmt(m.gmv),
      desc: 'جمع قیمت کالاهای فروخته‌شده قبل از تخفیف — فقط سفارش‌های پرداخت‌شده، بدون لغو/مرجوع',
    },
    {
      title: 'تخفیف داده‌شده',
      value: fmt(m.totalDiscount),
      desc: 'جمع تخفیف‌های اعمال‌شده روی آیتم‌های فروخته‌شده در بازه',
    },
    {
      title: 'فروش خالص',
      value: fmt(netSales),
      desc: 'GMV منهای تخفیف = مبلغی که خریداران واقعاً بابت کالا پرداختند',
      color: 'var(--color-primary)',
    },
    {
      title: 'مالیات دریافتی',
      value: fmt(m.tax),
      desc: 'مالیات قلم‌به‌قلم اقلام فعال بازه (داخل قیمت — بدهی به سازمان مالیاتی؛ گزارش کامل در «حسابداری → مالیات»)',
    },
    {
      title: 'بازگشت وجه به خریداران',
      value: fmt(m.refunds),
      desc: `اطلاعاتی: کیف پول ${fmt(m.refundsBreakdown?.wallet)} + استرداد از درگاه به کارت ${fmt(m.refundsBreakdown?.gateway)} — از سهم فروشنده کسر می‌شود، هزینهٔ پلتفرم نیست؛ اقلام مرجوعی از فروش بالا هم حذف شده‌اند`,
      color: 'var(--color-warning-dark)',
      drillParams: { source: 'user_wallet', type: 'refund' },
    },
    {
      title: 'درآمد خالص دوره',
      value: fmt(m.netRevenue),
      desc: 'برابر فروش خالص — مالیات داخل قیمت است و جداگانه اضافه نمی‌شود (اقلام لغو/مرجوع حذف شده‌اند؛ رفاند جداگانه و اطلاعاتی است)',
      color: 'var(--color-success-dark)',
    },
  ];

  const profitRows: MetricRow[] = [
    {
      title: 'کارمزد تحقق‌یافته',
      value: fmt(m.commission?.realized),
      desc: 'جمع کارمزد برداشت‌های «تکمیل‌شده» فروشندگان در بازه — در این پلتفرم کمیسیون موقع فروش صفر است و هنگام برداشت کسر می‌شود (در drill، مبلغ هر ردیف کلِ برداشت است و کارمزد داخل آن)',
      color: 'var(--color-success-dark)',
      drillParams: { source: 'seller_withdrawal', status: 'completed' },
    },
    {
      title: 'کارمزد در جریان (مورد انتظار)',
      value: fmt(m.commission?.estimated),
      desc: 'جمع کارمزد برداشت‌های در انتظار/تأییدشده/در حال پردازش — با تکمیل آن‌ها به «تحقق‌یافته» منتقل می‌شود (لحظه‌ای)',
      color: 'var(--color-warning-dark)',
      drillParams: { source: 'seller_withdrawal', status: 'pending' },
    },
    {
      title: 'کارمزد اقساطی دریافتی از خریداران',
      value: fmt(m.profit?.surchargeCollected),
      desc: `کارمزد خرید اقساطی اسنپ‌پی/دیجی‌پی که خریداران روی مبلغ سفارش پرداخت کرده‌اند (${m.profit?.surchargeCount ?? 0} پرداخت؛ مرحلهٔ اقساطی پرداخت ترکیبی هم شامل است) — خارج از GMV/فروش خالص و سهم فروشنده؛ کارمزدهای برگشتی (لغو کامل/ریورس/تعدیل) کسر شده`,
      color: 'var(--color-success-dark)',
      drillParams: { source: 'gateway', type: 'gateway_payment' },
    },
    {
      title: 'هزینه درگاه پرداخت',
      value: fmt(m.profit?.gatewayFees),
      desc: 'کارمزد درگاه روی پرداخت‌های موفق بازه — زرین‌پال/پی‌پینگ fee را در لاگ ثبت نمی‌کنند (صفر)؛ برای دیجی‌پی اقساطی برآورد = کارمزد دریافتی از خریدار (تا صورت‌حساب دیجی‌پی قطعی شود)',
      color: 'var(--color-error-dark)',
    },
    {
      title: 'هزینه ریفرال',
      value: fmt(m.profit?.referralCost),
      desc: 'پاداش‌های ریفرال پرداخت‌شده + متعهدشده برای برداشت‌های در حال پردازش',
      color: 'var(--color-error-dark)',
      drillParams: { source: 'gateway', type: 'referral_bonus_cost' },
    },
    {
      title: 'سود خالص تحقق‌یافته',
      value: fmt(m.profit?.realized),
      desc: 'کارمزد تحقق‌یافته − هزینه درگاه − هزینه ریفرال (سایر هزینه‌های عملیاتی لحاظ نشده)',
      color: 'var(--color-primary)',
    },
  ];

  const snapshotRows: MetricRow[] = [
    {
      title: 'وجه در گارانتی (اسکرو)',
      value: fmt(m.escrowPending),
      desc: 'سهم فروشندگان از فروش‌های تحویل‌شده که هنوز دوره گارانتی‌شان تمام نشده — تاریخ مبنا: تحویل واقعی',
      color: 'var(--color-warning-dark)',
    },
    {
      title: 'قابل برداشت فروشندگان',
      value: fmt(m.availableBalance),
      desc: `سهم آزادشدهٔ فروشندگان منهای برداشت‌های تکمیل‌شده (${fmt(m.sellerDeductions?.totalWithdrawn)})، در جریان (${fmt(m.sellerDeductions?.pendingWithdrawals)})، جریمه‌ها (${fmt(m.sellerDeductions?.penalties)}) و تعهدهای استرداد — برآورد سریع؛ عدد دقیق هر فروشنده در «حسابداری → بدهی فروشندگان»`,
      color: 'var(--color-success-dark)',
      drillParams: { source: 'seller_withdrawal' },
    },
    {
      title: 'وجوه مسدود',
      value: fmt(m.blockedFunds),
      desc: 'سهم فروشنده در سفارش‌های پرداخت‌شدهٔ دارای اختلاف فعال — تا تعیین تکلیف قفل است (سفارش پرداخت‌نشده یا مرجوع‌شده مسدود نیست)',
      color: 'var(--color-error-dark)',
    },
    {
      title: 'بدهی کیف‌پول کاربران',
      value: fmt(m.liabilities?.wallet),
      desc: 'جمع موجودی فعلی همه کیف‌پول‌ها = پولی که پلتفرم به کاربران بدهکار است',
      color: 'var(--color-error-dark)',
    },
    {
      title: 'بدهی پوینت‌ها (فیگاپوینت)',
      value: fmt(m.liabilities?.points),
      desc: 'امتیازهای مصرف‌نشده کاربران × نرخ تبدیل فعلی',
    },
    {
      title: 'تعهدهای استرداد باز به خریداران',
      value: fmt(m.liabilities?.refundObligations),
      desc: `${fmtCount(m.liabilities?.refundObligationsCount)} تعهد در انتظار تأمین وجه از ورودی‌های فروشنده — پول خریدار هنوز پرداخت نشده`,
      color: 'var(--color-error-dark)',
    },
    {
      title: 'وجوه نقدی پرداخت ترکیبی در انتظار مرحلهٔ دوم',
      value: fmt(m.liabilities?.splitHeld),
      desc: `بخش نقدی (زرین‌پال) سفارش‌های ترکیبی که مرحلهٔ اقساطی‌شان هنوز انجام نشده (${m.liabilities?.splitHeldCount ?? 0} سفارش) — این پول دریافت شده ولی متعلق به خریدار است: با تکمیل مرحلهٔ دوم به فروش تبدیل می‌شود و با انصراف/پایان مهلت خودکار به کیف پول او برمی‌گردد`,
      color: 'var(--color-warning-dark)',
      drillParams: { source: 'gateway', type: 'gateway_payment_split' },
    },
  ];

  const sideRevenueRows: MetricRow[] = [
    {
      title: 'درآمد بنر — در بازه',
      value: (
        <>
          {fmt(m.banner?.rangeRevenue)}
          <span style={{ fontSize: 11, color: 'var(--color-text-disabled)', marginRight: 6 }}>
            ({fmtCount(m.banner?.rangeCount)} رزرو)
          </span>
        </>
      ),
      desc: 'رزروهای بنر پرداخت‌شده در این بازه (مبلغ نهایی با مالیات)',
      drillParams: { source: 'banner' },
    },
    {
      title: 'درآمد بنر — کل ناخالص / خالص',
      value: (
        <>
          {fmt(m.banner?.revenue)}
          <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginRight: 6 }}>
            / خالص {fmt(m.banner?.netRevenue)}
          </span>
        </>
      ),
      desc: `کل تاکنون (${fmtCount(m.banner?.count)} رزرو) — خالص = ناخالص − مالیات (${fmt(m.banner?.tax)}) − استرداد (${fmt(m.banner?.refunded)})`,
    },
    {
      title: 'هزینه بازرسی دریافتی — در بازه',
      value: (
        <>
          {fmt(m.inspection?.feesTotal)}
          <span style={{ fontSize: 11, color: 'var(--color-text-disabled)', marginRight: 6 }}>
            ({fmtCount(m.inspection?.feesCount)} مورد)
          </span>
        </>
      ),
      desc: 'هزینه بازرسی پرداخت‌شده توسط خریداران روی آیتم‌های فروخته‌شده بازه (بدون لغو/مرجوع)',
    },
    {
      title: 'سهم پلتفرم از بازرسی — در بازه',
      value: fmt(m.inspection?.platformShare),
      desc: `سهم پلتفرم (پیش‌فرض ۳۰٪) از ${fmtCount(m.inspection?.completedCount)} بازرسی تکمیل‌شده — بقیه سهم بازرس است`,
      color: 'var(--color-success-dark)',
    },
    {
      title: 'سهم بازرسِ مسدود (شکایت فعال)',
      value: (
        <>
          {fmt(m.inspection?.blockedAmount)}
          <span style={{ fontSize: 11, color: 'var(--color-text-disabled)', marginRight: 6 }}>
            ({fmtCount(m.inspection?.blockedCount)} مورد)
          </span>
        </>
      ),
      desc: 'سهم بازرس از بازرسی‌هایی که شکایت فعال دارند — تا حل اختلاف قفل است (لحظه‌ای، مستقل از بازه)',
      color: 'var(--color-error-dark)',
    },
    {
      title: 'جریمه‌های بازرسی (کل تاکنون)',
      value: (
        <>
          <span style={{ color: 'var(--color-warning-dark)' }}>در انتظار {fmt(m.inspection?.penalties?.pending)}</span>
          <span style={{ margin: '0 6px' }}>·</span>
          <span style={{ color: 'var(--color-success-dark)' }}>تأیید {fmt(m.inspection?.penalties?.approved)}</span>
          <span style={{ margin: '0 6px' }}>·</span>
          <span style={{ color: 'var(--color-primary)' }}>بخشوده {fmt(m.inspection?.penalties?.waived)}</span>
        </>
      ),
      desc: 'جریمه تأخیر بازرسی/ارسال و جریمه شکایت — فقط «تأییدشده» از موجودی بازرس کسر می‌شود',
      drillParams: { source: 'inspection_penalty' },
    },
  ];

  // -------------------------------------------------------------------------
  // Rendering helpers
  // -------------------------------------------------------------------------

  /** 🧾 تفکیک درگاه‌ها + خلاصهٔ پرداخت ترکیبی — همان اعدادی که حسابدار برای تطبیق با صورت‌حساب هر درگاه لازم دارد */
  const renderGatewayBreakdown = () => {
    const gw: any = (m as any).gateways || {};
    const rows: any[] = gw.rows || [];
    const t: any = gw.totals || {};
    const sp: any = (m as any).split || {};
    const completed: any[] = sp.completed || [];
    const has = rows.length > 0;
    return (
      <div style={{ display: 'grid', gap: 14 }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="entity-table">
            <thead>
              <tr>
                <th>درگاه</th>
                <th>پرداخت‌های موفق</th>
                <th>مبلغ دریافتی</th>
                <th>ارزش کالا</th>
                <th>کارمزد اقساطی (خالص)</th>
                <th>استرداد از درگاه</th>
                <th>برگشت به کیف پول</th>
                <th>خالص درگاه</th>
                <th>سهم پرداخت ترکیبی</th>
              </tr>
            </thead>
            <tbody>
              {!has && (
                <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--color-text-secondary)' }}>در این بازه پرداخت درگاهی ثبت نشده است</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.provider} onClick={() => drill({ source: 'gateway' })} style={{ cursor: 'pointer' }} title="کلیک: اسناد درگاه‌ها در دفتر تراکنش‌ها">
                  <td style={{ fontWeight: 700 }}>{r.label}</td>
                  <td>{fmtCount(r.paymentsCount)}</td>
                  <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt(r.paymentsAmount)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmt(r.goodsAmount)}</td>
                  <td style={{ whiteSpace: 'nowrap', color: 'var(--color-success-dark)' }}>
                    {fmt(r.surchargeNet)}
                    {r.surchargeCount > 0 && <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{fmtCount(r.surchargeCount)} پرداخت اقساطی{r.surchargeGross !== r.surchargeNet ? ` · ناخالص ${fmt(r.surchargeGross)}` : ''}</div>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap', color: 'var(--color-error-dark)' }}>
                    {fmt(r.refundsAmount)}
                    {r.refundsCount > 0 && <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{fmtCount(r.refundsCount)} استرداد{r.refundsSurchargeReturned > 0 ? ` · شامل کارمزد ${fmt(r.refundsSurchargeReturned)}` : ''}</div>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap', color: 'var(--color-warning-dark)' }}>
                    {fmt(r.walletBackAmount)}
                    {r.walletBackCount > 0 && <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{fmtCount(r.walletBackCount)} مورد — پول نزد پلتفرم ماند</div>}
                  </td>
                  <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt(r.netAmount)}</td>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {r.split?.paymentsCount > 0 ? (
                      <>
                        <div>{fmtCount(r.split.paymentsCount)} مرحله · {fmt(r.split.paymentsAmount)}</div>
                        {r.split.cashAmount > 0 && <div style={{ color: 'var(--color-text-secondary)' }}>نقدی: {fmt(r.split.cashAmount)}</div>}
                        {r.split.bnplAmount > 0 && <div style={{ color: 'var(--color-text-secondary)' }}>اقساطی: {fmt(r.split.bnplAmount)}</div>}
                        {r.split.refundsAmount > 0 && <div style={{ color: 'var(--color-error-dark)' }}>استرداد: {fmt(r.split.refundsAmount)}</div>}
                        {r.split.walletBackAmount > 0 && <div style={{ color: 'var(--color-warning-dark)' }}>به کیف پول: {fmt(r.split.walletBackAmount)}</div>}
                      </>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
            {has && (
              <tfoot>
                <tr style={{ fontWeight: 700, background: 'var(--color-bg-hover)' }}>
                  <td>جمع</td>
                  <td>{fmtCount(t.paymentsCount)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmt(t.paymentsAmount)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmt((t.paymentsAmount || 0) - (rows.reduce((s, r) => s + (r.surchargeGross || 0), 0)))}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmt(t.surchargeNet)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmt(t.refundsAmount)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmt(t.walletBackAmount)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmt(t.netAmount)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.9 }}>
          <b>راهنما:</b> «مبلغ دریافتی» = آنچه درگاه تأیید کرد (برای اقساطی شامل کارمزد خریدار). «ارزش کالا» = دریافتی منهای کارمزد اقساطی = مبنای فروش و سهم فروشنده.
          «استرداد از درگاه» = پولی که واقعاً از درگاه به خریدار برگشت (لغو/کاهش مبلغ اسنپ‌پی، reverse/refund دیجی‌پی). «برگشت به کیف پول» = پول نزد پلتفرم ماند و به کیف پول خریدار منتقل شد (بدهی؛ مثل بخش نقدی پرداخت ترکیبیِ لغوشده).
          «سهم پرداخت ترکیبی» = مراحل سفارش‌های ترکیبی که از این درگاه گذشتند (نقدی از زرین‌پال، اقساطی از اسنپ‌پی/دیجی‌پی).
        </div>
        <table className="entity-table">
          <thead>
            <tr>
              <th style={{ width: '30%' }}>پرداخت ترکیبی (نقدی + اقساطی)</th>
              <th style={{ width: '20%' }}>تعداد</th>
              <th>مبالغ</th>
            </tr>
          </thead>
          <tbody>
            {completed.length === 0 && (
              <tr>
                <td style={{ fontWeight: 600 }}>تکمیل‌شده در بازه</td>
                <td>۰</td>
                <td style={{ color: 'var(--color-text-secondary)' }}>—</td>
              </tr>
            )}
            {completed.map((c) => (
              <tr key={c.gateway}>
                <td style={{ fontWeight: 600 }}>تکمیل‌شده — اقساطی با {c.label}</td>
                <td>{fmtCount(c.count)} سفارش</td>
                <td style={{ fontSize: 12 }}>
                  ارزش کالا {fmt(c.goods)} · قابل پرداخت {fmt(c.payable)}{c.surcharge > 0 ? ` (شامل کارمزد ${fmt(c.surcharge)})` : ''}{c.refunded > 0 ? ` · مسترد‌شده ${fmt(c.refunded)}` : ''}
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ fontWeight: 600 }}>لغو/منقضی‌شده در بازه</td>
              <td>{fmtCount(sp.cancelled?.count || 0)} سفارش</td>
              <td style={{ fontSize: 12 }}>بخش نقدیِ برگشتی به کیف پول: {fmt(sp.cancelled?.refundedToWallet || 0)}</td>
            </tr>
            <tr onClick={() => drill({ source: 'gateway', type: 'gateway_payment_split' })} style={{ cursor: 'pointer' }} title="کلیک: اسناد پرداخت ترکیبی در دفتر تراکنش‌ها">
              <td style={{ fontWeight: 600 }}>در انتظار مرحلهٔ دوم (لحظه‌ای)</td>
              <td>{fmtCount(sp.held?.count || 0)} سفارش</td>
              <td style={{ fontSize: 12, color: 'var(--color-warning-dark)' }}>وجه نقدیِ نگه‌داشته‌شده: {fmt(sp.held?.amount || 0)} — متعلق به خریدار تا تکمیل یا برگشت</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const renderMetricTable = (rows: MetricRow[]) => (
    <table className="entity-table">
      <thead>
        <tr>
          <th style={{ width: '26%' }}>شاخص</th>
          <th style={{ width: '26%' }}>مقدار</th>
          <th>این عدد دقیقاً چیست؟</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={r.title}
            onClick={r.drillParams ? () => drill(r.drillParams!) : undefined}
            style={r.drillParams ? { cursor: 'pointer' } : undefined}
            title={r.drillParams ? 'کلیک: نمایش اسناد این شاخص در دفتر تراکنش‌ها' : undefined}
          >
            <td style={{ fontWeight: 600 }}>
              {r.title}
              {r.drillParams && (
                <DrillIcon style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 5, color: 'var(--color-primary)' }} />
              )}
            </td>
            <td style={{ fontWeight: 700, whiteSpace: 'nowrap', color: r.color }}>{r.value}</td>
            <td style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{r.desc}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const section = (icon: React.ReactNode, title: string, subtitle: string, body: React.ReactNode) => (
    <div className="edit-card">
      <div className="edit-card-header">
        {icon}
        <span style={{ marginRight: 6 }}>{title}</span>
        <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--color-text-secondary)', marginRight: 8 }}>
          {subtitle}
        </span>
      </div>
      <div className="edit-card-body" style={{ overflowX: 'auto' }}>{body}</div>
    </div>
  );

  const iconSx = { fontSize: 18, verticalAlign: 'middle' } as const;

  // KPI cards
  const mainStats = [
    { title: 'ارزش ناخالص معاملات (GMV)', value: m.gmv, icon: <TrendingUpIcon />, color: 'blue', desc: 'فروش قبل از تخفیف — در بازه' },
    { title: 'درآمد خالص دوره', value: m.netRevenue, icon: <AccountBalanceIcon />, color: 'green', desc: 'فروش خالص — مالیات داخل قیمت (بدون اقلام مرجوعی)' },
    { title: 'کارمزد تحقق‌یافته', value: m.commission?.realized, icon: <PercentIcon />, color: 'orange', desc: 'درآمد قطعی پلتفرم — در بازه' },
    { title: 'سود خالص تحقق‌یافته', value: m.profit?.realized, icon: <PosIcon />, color: 'purple', desc: 'کارمزد − درگاه − ریفرال' },
    { title: 'وجه در گارانتی', value: m.escrowPending, icon: <LockIcon />, color: 'red', desc: 'لحظه‌ای — مستقل از بازه' },
    { title: 'قابل برداشت فروشندگان', value: m.availableBalance, icon: <WalletIcon />, color: 'blue', desc: 'لحظه‌ای — مستقل از بازه' },
  ];

  const calendarTotal = calendar.reduce((s, c) => s + (c.amount || 0), 0);

  return (
    <ProtectedRoute requiredPermissions={['admin_finance_overview']}>
      <Helmet>
        <title>داشبورد مالی</title>
      </Helmet>

      <div className="entity-page" dir="rtl">
        {/* Header */}
        <div className="entity-header">
          <div>
            <h1 className="entity-title">داشبورد مالی</h1>
            <p className="entity-subtitle">
              نمای کلی وضعیت مالی — شاخص‌های «در بازه» تابع بازه انتخابی‌اند و شاخص‌های «لحظه‌ای» وضعیت همین الان را نشان می‌دهند
            </p>
          </div>
          <div className="header-actions">
            <select className="header-btn" value={range} onChange={(e) => setRange(e.target.value as any)} style={{ cursor: 'pointer' }}>
              <option value="7d">۷ روز اخیر</option>
              <option value="30d">۳۰ روز اخیر</option>
              <option value="90d">۹۰ روز اخیر</option>
              <option value="custom">بازه دلخواه…</option>
            </select>
            <select className="header-btn" value={unit} onChange={(e) => setUnit(e.target.value as CurrencyBase)} style={{ cursor: 'pointer' }}>
              <option value="IRT">تومان</option>
              <option value="IRR">ریال</option>
            </select>
            <button className="header-btn refresh" onClick={fetchData} disabled={loading} title="بروزرسانی">
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </button>
          </div>
        </div>

        {/* Custom Jalali range */}
        {range === 'custom' && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
            <JalaliDateField label="از تاریخ (شمسی)" value={customFromJ} onChange={setCustomFromJ} />
            <JalaliDateField label="تا تاریخ (شمسی)" value={customToJ} onChange={setCustomToJ} />
            <Button size="small" variant="contained" onClick={applyCustomRange} disabled={loading}>
              اعمال بازه
            </Button>
            {range === 'custom' && !appliedCustom && (
              <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                تاریخ شمسی را به شکل ۱۴۰۴/۰۱/۰۱ وارد کنید
              </span>
            )}
          </div>
        )}

        {/* Quick links to the new module */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <Button size="small" variant="outlined" startIcon={<LedgerIcon />} onClick={() => drill({})}>
            دفتر تراکنش‌ها
          </Button>
          <Button size="small" variant="outlined" startIcon={<StatementIcon />} onClick={() => navigate('/finance/statements')}>
            گردش حساب
          </Button>
          <Button size="small" variant="outlined" startIcon={<ReportIcon />} onClick={() => navigate('/finance/reports')}>
            گزارش‌های دوره‌ای
          </Button>
          <Button size="small" variant="contained" startIcon={<AccountBalanceIcon />} onClick={() => navigate('/finance/accounting')}>
            حسابداری (سود و زیان، ترازنامه، مالیات، مغایرت‌گیری)
          </Button>
          <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', alignSelf: 'center' }}>
            بازه فعال: {toPersianDigits(jalaliRange.from)} تا {toPersianDigits(jalaliRange.to)}
          </span>
        </div>

        {/* KPI cards */}
        <div className="entity-stats">
          {mainStats.map((stat, index) => (
            <div key={index} className="stat-card">
              <div className={`stat-icon ${stat.color}`}>{stat.icon}</div>
              <div className="stat-content">
                <div className="stat-label">{stat.title}</div>
                <div className="stat-value">{stat.value != null ? fmt(stat.value) : '—'}</div>
                <div style={{ fontSize: 10, color: 'var(--color-text-disabled)', marginTop: 2 }}>{stat.desc}</div>
              </div>
            </div>
          ))}
        </div>

        {loading && !overview ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : (
          <div className="edit-form">
            {/* 1 — Sales & revenue (range) */}
            {section(
              <PaymentsIcon style={iconSx} />,
              'فروش و درآمد',
              'در بازه انتخابی · سطرهای دارای آیکن، قابل کلیک به دفتر تراکنش‌ها',
              renderMetricTable(salesRows),
            )}

            {/* 2 — Platform profit (range) */}
            {section(
              <PercentIcon style={iconSx} />,
              'کارمزد و سود پلتفرم',
              'در بازه انتخابی',
              renderMetricTable(profitRows),
            )}

            {/* 3 — Snapshot balances */}
            {section(
              <WalletIcon style={iconSx} />,
              'وضعیت موجودی و تعهدات',
              'لحظه‌ای — مستقل از بازه انتخابی',
              renderMetricTable(snapshotRows),
            )}

            {/* 3b — Gateway breakdown: چقدر از کدام درگاه آمد/برگشت */}
            {section(
              <LedgerIcon style={iconSx} />,
              'تفکیک درگاه‌های پرداخت',
              'در بازه انتخابی · زرین‌پال / اسنپ‌پی / دیجی‌پی — ورود، خروج، کارمزد اقساطی و سهم پرداخت ترکیبی',
              renderGatewayBreakdown(),
            )}

            {/* 4 — Ledger turnover by source (NEW — from the unified ledger) */}
            {section(
              <LedgerIcon style={iconSx} />,
              'گردش دفتر تراکنش‌ها',
              'در بازه انتخابی · از ماژول دفتر تراکنش‌های یکپارچه',
              ledgerDenied ? (
                <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: 0 }}>
                  برای مشاهده این بخش به دسترسی «دفتر تراکنش‌های مالی» نیاز دارید.
                </p>
              ) : !ledgerSummary ? (
                <div style={{ textAlign: 'center', padding: 12 }}><CircularProgress size={20} /></div>
              ) : (
                <table className="entity-table">
                  <thead>
                    <tr>
                      <th>منبع</th>
                      <th>بستانکار (+)</th>
                      <th>بدهکار (−)</th>
                      <th>خالص</th>
                      <th>تعداد اسناد</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerSummary.bySource.map((s) => (
                      <tr
                        key={s.source}
                        onClick={() => drill({ source: s.source })}
                        style={{ cursor: 'pointer' }}
                        title="کلیک: نمایش اسناد این منبع"
                      >
                        <td style={{ fontWeight: 600 }}>
                          {SOURCE_LABELS[s.source as LedgerSource] || s.source}
                          <DrillIcon style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 5, color: 'var(--color-primary)' }} />
                        </td>
                        <td style={{ color: 'var(--color-success-dark)', whiteSpace: 'nowrap' }}>{fmt(s.credit)}</td>
                        <td style={{ color: 'var(--color-error-dark)', whiteSpace: 'nowrap' }}>{fmt(s.debit)}</td>
                        <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt(s.credit - s.debit)}</td>
                        <td>{fmtCount(s.count)}</td>
                        <td></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ),
            )}
            {/* AUDIT CRIT-6: no cross-source grand total — a single real-world event
                (e.g. wallet charge, card order) legitimately appears in 2+ sources,
                so summing across sources is not an accounting figure. */}
            {ledgerSummary && !ledgerDenied && (
              <p style={{ fontSize: 11, color: 'var(--color-text-secondary)', margin: '4px 4px 0' }}>
                ℹ️ هر منبع، دفتر حسابِ خودش است و یک رویداد واقعی (مثل پرداخت سفارش) به‌درستی در چند منبع ثبت می‌شود؛
                به همین دلیل «جمع بین‌منبعی» نمایش داده نمی‌شود. برای تحلیل، هر منبع را جداگانه بخوانید.
              </p>
            )}

            {/* 5 — Side revenues */}
            {section(
              <ExchangeIcon style={iconSx} />,
              'درآمدهای جانبی',
              'بنر · بازرسی',
              renderMetricTable(sideRevenueRows),
            )}

            {/* 6 — Release calendar */}
            {section(
              <CalendarIcon style={iconSx} />,
              'تقویم آزادسازی اسکرو',
              `۳۰ روز آینده · جمع: ${fmt(calendarTotal)}`,
              calendar.length === 0 ? (
                <div className="entity-empty">
                  <div className="entity-empty-icon"><EventNoteIcon style={{ fontSize: 48 }} /></div>
                  <p className="entity-empty-text">هیچ آزادسازی برای روزهای آینده ثبت نشده است</p>
                </div>
              ) : (
                <table className="entity-table">
                  <thead>
                    <tr>
                      <th>تاریخ</th>
                      <th>مبلغ آزادشونده</th>
                      <th>فروشگاه‌ها</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calendar.map((c) => (
                      <tr key={c.date}>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <div style={{ fontWeight: 600 }}>
                            {toPersianDigits(moment(c.date, 'YYYY-MM-DD').format('jYYYY/jMM/jDD'))}
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--color-text-disabled)', fontFamily: 'monospace' }}>
                            {c.date}
                          </div>
                        </td>
                        <td style={{ fontWeight: 700, color: 'var(--color-success-dark)', whiteSpace: 'nowrap' }}>{fmt(c.amount)}</td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {(c.stores || []).slice(0, 3).map((s: any) => (
                              <div key={String(s.storeId)} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                <Tooltip title="گردش حساب فروشگاه">
                                  <span
                                    style={{ cursor: 'pointer', color: 'var(--color-primary)' }}
                                    onClick={() => navigate(`/finance/statements?storeId=${s.storeId}`)}
                                  >
                                    {s.storeName}
                                  </span>
                                </Tooltip>
                                <span style={{ color: 'var(--color-text-secondary)' }}>{fmt(s.amount)}</span>
                              </div>
                            ))}
                            {(c.stores || []).length > 3 && (
                              <div style={{ color: 'var(--color-primary)', fontSize: 12 }}>
                                +{toPersianDigits(c.stores.length - 3)} فروشگاه دیگر
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ),
            )}
          </div>
        )}
      </div>
    </ProtectedRoute>
  );
};

export default FinanceDashboard;
